<template>
  <div>
    <!-- capacitor app update checker -->
    <el-dialog
      :model-value="isAppStoreUpdateAvailable"
      width="580px"
      top="9vh"

      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      modal-class="bg-light [&_.el-dialog\_\_body]:pt-0 [&_.el-dialog\_\header]:px-5"

      @accept="openAppStore"
      @dismiss="() => isAppStoreUpdateAvailable = false"
    >
      <template #header>
        <div class="DialogTitle">
          <h4 class="text-primary-500">
            <span>Aplikace vyžaduje aktualizaci</span>
          </h4>
        </div>
      </template>

      <div class="text-base px-5">
        <p class="mb-4">
          Pro správné fungování Tiché linky prosím aktualizujte aplikaci co nejdříve.
        </p>

        <iframe
          width="560"
          height="210"
          class="max-w-full"
          src="https://app.tichalinka.cz/youtube.html?v=fUJaTMBfjug&autoplay=1&loop=1&mute=1&playlist=fUJaTMBfjug"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </div>

      <slot name="footer">
        <span
          class="flex items-center justify-between px-5 mt-8 flex-col-reverse sm:flex-row gap-y-4"
        >
          <el-button
            size="large"
            class="el-button--no-shadow"
            @click="isAppStoreUpdateAvailable = false"
          >
            Ignorovat
          </el-button>
          <div
            class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
          >
            <el-button
              size="large"
              type="primary"
              class="px-5 w-full m-0"
              @click="openAppStore"
            >
              Aktualizovat
            </el-button>
          </div>
        </span>
      </slot>
    </el-dialog>
  </div>
</template>

<script setup>
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

const latestVersionResponse = ref(null);
const isAppStoreUpdateAvailable = ref(false);

// Utility to compare two semantic version strings (e.g. '2.2.10' < '2.2.11'). Returns -1, 0, or 1.
function compareVersions(v1, v2) {
  const a = v1.split('.').map(Number);
  const b = v2.split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const num1 = a[i] || 0;
    const num2 = b[i] || 0;
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  return 0;
}

const checkAppVersion = async(state) => {
  if (state?.isActive) {
    const baseUrl = 'https://app.tichalinka.cz';
    const versionSourceUrl = `${baseUrl}/ios/version.json`;

    // Plain `$fetch`, not `useApi`: this is a public static file on the web host,
    // not the Django API. `useApi` attaches `Authorization`, which turns this GET
    // into a CORS preflight — and `Authorization` is a CORS-non-wildcard header, so
    // the `Access-Control-Allow-Headers: *` in vercel.json does not cover it. The
    // check would fail for exactly the logged-in users, and `useApi` logs a user out
    // on any 401 it sees.
    const response = await $fetch(versionSourceUrl, { timeout: 10000 }).catch((error) => {
      console.error('[CapacitorAppUpdate] Error getting app latest version', error);

      return null;
    });

    // An unknown path on the web host answers with the SPA shell at HTTP 200 and
    // `text/html`, so a resolved promise proves nothing about what came back.
    // `appStoreUrl` is checked too, not just the version: without it the dialog
    // still opens and its Aktualizovat button leads nowhere. The scheme is pinned
    // rather than the host, so switching to `itms-apps://` — which opens the App
    // Store app instead of Safari — stays possible without touching this file.
    const isUsable = typeof response?.minimumVersion === 'string'
      && /^(https|itms-apps):\/\//.test(response?.appStoreUrl ?? '');

    latestVersionResponse.value = isUsable ? response : null;

    if (!latestVersionResponse.value) {
      return;
    }

    console.log('[CapacitorAppUpdate] Latest version response', latestVersionResponse.value);

    const nativeAppInfo = await App.getInfo();
    console.log('[CapacitorAppUpdate] Native app version', nativeAppInfo.version);

    if (
      latestVersionResponse.value?.minimumVersion &&
      nativeAppInfo.version &&
      compareVersions(nativeAppInfo.version, latestVersionResponse.value.minimumVersion) < 0
    ) {
      isAppStoreUpdateAvailable.value = true;
    }
  }
};

const openAppStore = () => {
  window.open(latestVersionResponse.value?.appStoreUrl, '_blank');
  isAppStoreUpdateAvailable.value = false;
};

onMounted(() => {
  if (Capacitor.getPlatform() === 'ios') {
    checkAppVersion({ isActive: true });

    App.addListener('appStateChange', (state) => {
      checkAppVersion(state);
    });
  }
});
</script>
